import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Chip, CircularProgress, Alert, IconButton, Tooltip, Stack,
  Grid, Divider, Button, Collapse, TextField,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import VerifiedIcon from '@mui/icons-material/Verified';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SendIcon from '@mui/icons-material/Send';
import { pactFootprintService, leiFromCompanyIds } from '../services/pactFootprintService';

/** Absent optionals are omitted by the serializer, not null — show an em dash. */
const fmt = (v) => (v === undefined || v === null || v === '' ? '—' : String(v));
const fmtBool = (v) => (v === undefined || v === null ? '—' : (v ? 'Yes' : 'No'));
const fmtDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString();
};

/** ELM error bodies vary ({error:{message}} vs {error} vs {message}) — always render a string. */
const errMessage = (err) =>
  err?.response?.data?.error?.message ||
  (typeof err?.response?.data?.error === 'string' ? err.response.data.error : null) ||
  err?.response?.data?.message ||
  err?.message ||
  String(err);

function Field({ label, mono, children }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontFamily: mono ? 'monospace' : undefined, wordBreak: 'break-word' }}>
        {children}
      </Typography>
    </Box>
  );
}

/**
 * PACT Footprint detail — the full WBCSD PACT v3 ProductFootprint document for a
 * single footprint, reached by clicking a row on the PACT Footprints list.
 */
function PACTFootprintDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [footprint, setFootprint] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [jsonOpen, setJsonOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setFootprint(await pactFootprintService.getFootprint(id));
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // ── Async dVE verification (Strategy B pub/sub) ──
  const [verification, setVerification] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState(null);
  const [targetNode, setTargetNode] = useState('1');
  const [resultOpen, setResultOpen] = useState(false);

  const loadVerification = useCallback(async () => {
    try {
      setVerification(await pactFootprintService.getVerification(id));
    } catch (err) {
      // 404 simply means nothing has been submitted yet
      if (err.response?.status !== 404) setVerifyError(errMessage(err));
    }
  }, [id]);

  useEffect(() => { loadVerification(); }, [loadVerification]);

  // Poll while the correlation row is still PENDING
  useEffect(() => {
    if (verification?.status !== 'PENDING') return undefined;
    const timer = setInterval(loadVerification, 4000);
    return () => clearInterval(timer);
  }, [verification, loadVerification]);

  const handleVerify = async () => {
    try {
      setVerifying(true);
      setVerifyError(null);
      const submitted = await pactFootprintService.verifyFootprint(id, {
        nodeId: Number(targetNode) || undefined,
      });
      setVerification(submitted);
    } catch (err) {
      setVerifyError(errMessage(err));
    } finally {
      setVerifying(false);
    }
  };

  if (loading) {
    return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
  }

  if (error || !footprint) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/pact-footprints')}>
          Back to PACT Footprints
        </Button>
        <Alert severity="error" sx={{ mt: 2 }}>
          {error || `Product footprint ${id} not found.`}
        </Alert>
      </Box>
    );
  }

  const fp = footprint;
  const pcf = fp.pcf || {};
  const lei = leiFromCompanyIds(fp.companyIds);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Tooltip title="Back to PACT Footprints">
          <IconButton onClick={() => navigate('/pact-footprints')}><ArrowBackIcon /></IconButton>
        </Tooltip>
        <Typography variant="h4" sx={{ flexGrow: 1 }}>
          {fp.productNameCompany || fp.id}
        </Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={load}><RefreshIcon /></IconButton>
        </Tooltip>
      </Box>
      <Stack direction="row" spacing={1} sx={{ mt: 1, mb: 2, flexWrap: 'wrap' }} useFlexGap>
        <Chip label={`status: ${fp.status}`} size="small" variant="outlined" />
        <Chip label={`PACT Tech Specs v${fp.specVersion}`} size="small" variant="outlined" />
        {lei
          ? <Chip label={`LEI: ${lei}`} size="small" color="success" icon={<VerifiedIcon />} />
          : <Chip label="No LEI" size="small" variant="outlined" />}
      </Stack>

      {/* ── PCF headline ── */}
      <Paper sx={{ p: 3, mb: 3, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <Typography variant="caption" color="text.secondary">
              PCF (excl. biogenic uptake)
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
              {fmt(pcf.pcfExcludingBiogenicUptake)}
              <Typography component="span" variant="h6" color="text.secondary" sx={{ ml: 1 }}>
                kgCO2e
              </Typography>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              per {fmt(pcf.declaredUnitAmount)} {fmt(pcf.declaredUnitOfMeasurement)}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Field label="PCF (incl. biogenic uptake)">
              {fmt(pcf.pcfIncludingBiogenicUptake)} kgCO2e
            </Field>
            <Field label="Fossil GHG emissions">{fmt(pcf.fossilGhgEmissions)} kgCO2e</Field>
          </Grid>
          <Grid item xs={12} md={4}>
            <Field label="Reference period">
              {fmt(pcf.referencePeriodStart)} → {fmt(pcf.referencePeriodEnd)}
            </Field>
            <Field label="Geography (country)">{fmt(pcf.geographyCountry)}</Field>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={3}>
        {/* ── Identification ── */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>Footprint identification</Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12}><Field label="id" mono>{fmt(fp.id)}</Field></Grid>
              <Grid item xs={6}><Field label="specVersion">{fmt(fp.specVersion)}</Field></Grid>
              <Grid item xs={6}><Field label="status">{fmt(fp.status)}</Field></Grid>
              <Grid item xs={12}><Field label="created">{fmtDate(fp.created)}</Field></Grid>
              <Grid item xs={6}><Field label="validityPeriodStart">{fmt(fp.validityPeriodStart)}</Field></Grid>
              <Grid item xs={6}><Field label="validityPeriodEnd">{fmt(fp.validityPeriodEnd)}</Field></Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* ── Company & product identifiers ── */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>Company & product identifiers</Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12}><Field label="companyName">{fmt(fp.companyName)}</Field></Grid>
              <Grid item xs={12}>
                <Field label="companyIds">
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                    {(fp.companyIds || []).length === 0 && <Typography variant="body2">—</Typography>}
                    {(fp.companyIds || []).map((cid) => {
                      const isLei = String(cid).toLowerCase().startsWith('urn:lei:');
                      return isLei ? (
                        <Chip key={cid} label={cid} size="small" color="success" variant="outlined" icon={<VerifiedIcon />} />
                      ) : (
                        <Chip key={cid} label={cid} size="small" variant="outlined" />
                      );
                    })}
                  </Stack>
                </Field>
              </Grid>
              <Grid item xs={12}>
                <Field label="productIds">
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                    {(fp.productIds || []).length === 0 && <Typography variant="body2">—</Typography>}
                    {(fp.productIds || []).map((pid) => (
                      <Chip key={pid} label={pid} size="small" variant="outlined" />
                    ))}
                  </Stack>
                </Field>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* ── Product ── */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Product</Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Field label="productDescription">{fmt(fp.productDescription)}</Field>
              </Grid>
              <Grid item xs={12}>
                <Field label="comment">{fmt(fp.comment)}</Field>
              </Grid>
              {pcf.boundaryProcessesDescription && (
                <Grid item xs={12}>
                  <Field label="pcf.boundaryProcessesDescription">
                    {pcf.boundaryProcessesDescription}
                  </Field>
                </Grid>
              )}
            </Grid>
          </Paper>
        </Grid>

        {/* ── PCF detail ── */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Product Carbon Footprint (pcf)</Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={6} md={3}>
                <Field label="pcfExcludingBiogenicUptake">{fmt(pcf.pcfExcludingBiogenicUptake)} kgCO2e</Field>
              </Grid>
              <Grid item xs={6} md={3}>
                <Field label="pcfIncludingBiogenicUptake">{fmt(pcf.pcfIncludingBiogenicUptake)} kgCO2e</Field>
              </Grid>
              <Grid item xs={6} md={3}>
                <Field label="fossilGhgEmissions">{fmt(pcf.fossilGhgEmissions)} kgCO2e</Field>
              </Grid>
              <Grid item xs={6} md={3}>
                <Field label="fossilCarbonContent">{fmt(pcf.fossilCarbonContent)}</Field>
              </Grid>
              <Grid item xs={6} md={3}>
                <Field label="declaredUnitOfMeasurement">{fmt(pcf.declaredUnitOfMeasurement)}</Field>
              </Grid>
              <Grid item xs={6} md={3}>
                <Field label="declaredUnitAmount">{fmt(pcf.declaredUnitAmount)}</Field>
              </Grid>
              <Grid item xs={6} md={3}>
                <Field label="productMassPerDeclaredUnit">
                  {fmt(pcf.productMassPerDeclaredUnit)} kg
                </Field>
              </Grid>
              <Grid item xs={6} md={3}>
                <Field label="exemptedEmissionsPercent">
                  {pcf.exemptedEmissionsPercent === undefined ? '—' : `${pcf.exemptedEmissionsPercent} %`}
                </Field>
              </Grid>
              <Grid item xs={6} md={3}>
                <Field label="primaryDataShare">
                  {pcf.primaryDataShare === undefined ? '—' : `${pcf.primaryDataShare} %`}
                </Field>
              </Grid>
              <Grid item xs={6} md={3}>
                <Field label="packagingEmissionsIncluded">{fmtBool(pcf.packagingEmissionsIncluded)}</Field>
              </Grid>
              <Grid item xs={6} md={3}>
                <Field label="referencePeriodStart">{fmt(pcf.referencePeriodStart)}</Field>
              </Grid>
              <Grid item xs={6} md={3}>
                <Field label="referencePeriodEnd">{fmt(pcf.referencePeriodEnd)}</Field>
              </Grid>
              <Grid item xs={12} md={6}>
                <Field label="ipccCharacterizationFactors">
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                    {(pcf.ipccCharacterizationFactors || []).length === 0 && <Typography variant="body2">—</Typography>}
                    {(pcf.ipccCharacterizationFactors || []).map((f) => (
                      <Chip key={f} label={f} size="small" variant="outlined" />
                    ))}
                  </Stack>
                </Field>
              </Grid>
              <Grid item xs={12} md={6}>
                <Field label="crossSectoralStandards">
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                    {(pcf.crossSectoralStandards || []).length === 0 && <Typography variant="body2">—</Typography>}
                    {(pcf.crossSectoralStandards || []).map((s) => (
                      <Chip key={s} label={s} size="small" color="info" variant="outlined" />
                    ))}
                  </Stack>
                </Field>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* ── dVE verification (async PACT pub/sub) ── */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Independent verification (dVE, async PACT)</Typography>
            <Divider sx={{ mb: 2 }} />
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
              <TextField
                size="small" type="number" label="PACT directory node"
                value={targetNode}
                onChange={(e) => setTargetNode(e.target.value)}
                inputProps={{ min: 1, step: 1 }}
                helperText="Node the verified footprint (+DME) is re-published to"
                sx={{ width: 240 }}
              />
              <Button
                variant="contained" color="primary" startIcon={<SendIcon />}
                onClick={handleVerify} disabled={verifying}
              >
                {verifying ? 'Submitting to dVE...' : 'Verify with dVE'}
              </Button>
              {verification && (() => {
                // Transport status (PENDING/COMPLETED/ERROR) only says the round-trip
                // finished; the verdict inside the result says what dVE concluded.
                // Headline the verdict once known so FLAGGED never reads as "completed".
                const verdict = verification.status === 'COMPLETED'
                  ? String(verification.result?.verificationResult?.verification_status || '').toUpperCase()
                  : '';
                const label = verdict === 'FLAGGED'
                  ? 'verification: FLAGGED - review pending in dVE'
                  : verdict
                    ? `verification: ${verdict}`
                    : `verification: ${verification.status}`;
                const color = verification.status === 'ERROR' || verdict === 'REJECTED'
                  ? 'error'
                  : verdict === 'FLAGGED'
                    ? 'warning'
                    : verification.status === 'COMPLETED' ? 'success' : 'warning';
                return <Chip label={label} size="small" color={color} />;
              })()}
            </Stack>
            {verifyError && <Alert severity="error" sx={{ mt: 2 }}>{verifyError}</Alert>}
            {verification?.status === 'PENDING' && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Waiting for dVE on the Pub/Sub bus (correlation {verification.correlation_id}). This page polls automatically.
              </Alert>
            )}
            {verification?.status === 'ERROR' && (
              <Alert severity="error" sx={{ mt: 2 }}>{verification.error || 'Verification failed.'}</Alert>
            )}
            {verification?.status === 'COMPLETED' && (
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}><Field label="Verified at">{fmtDate(verification.verified_at)}</Field></Grid>
                  <Grid item xs={12} md={4}><Field label="Re-published to node">{fmt(verification.node_id)}</Field></Grid>
                  <Grid item xs={12} md={4}><Field label="Correlation id" mono>{fmt(verification.correlation_id)}</Field></Grid>
                </Grid>
                <Button size="small" sx={{ mt: 1 }} onClick={() => setResultOpen(!resultOpen)}>
                  {resultOpen ? 'Hide dVE result' : 'Show dVE result'}
                </Button>
                <Collapse in={resultOpen}>
                  <Box
                    component="pre"
                    sx={{
                      mt: 1, p: 2, bgcolor: 'grey.900', color: 'grey.100', borderRadius: 1,
                      fontSize: '0.8rem', overflow: 'auto', maxHeight: 480,
                    }}
                  >
                    {JSON.stringify(verification.result, null, 2)}
                  </Box>
                </Collapse>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* ── Raw PACT document ── */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h6">Raw PACT ProductFootprint JSON</Typography>
              <IconButton onClick={() => setJsonOpen(!jsonOpen)} size="small">
                {jsonOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
            <Collapse in={jsonOpen}>
              <Box
                component="pre"
                sx={{
                  mt: 1, p: 2, bgcolor: 'grey.900', color: 'grey.100', borderRadius: 1,
                  fontSize: '0.8rem', overflow: 'auto', maxHeight: 480,
                }}
              >
                {JSON.stringify(fp, null, 2)}
              </Box>
            </Collapse>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default PACTFootprintDetail;
